import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Fab,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { colors } from "brand";
import { PinLock, TableSkeleton, logoutCognito } from "common-fe";
import { useTranslation } from "i18n-fe";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useRidesForRing } from "../hooks/useRidesForRing";
import { useBreaksForRing } from "../hooks/useBreaksForRing";
import { usePeople, useHorses, useTestsForRides } from "../hooks/useRoster";
import { useRideStatuses } from "../hooks/useRideStatuses";
import { useLastPrefetchedAt } from "../hooks/useLastPrefetchedAt";
import {
  buildOrderOfGoRows,
  findNextEvent,
  findRoleRideId,
  ROW_KIND,
  RIDE_ROLE,
} from "../utils/orderOfGo";
import { formatTime, formatVenueClock } from "../utils/formatTime";
import { formatClockTime } from "../utils/formatClock";
import RideRow from "./RideRow";
import ClassResults from "./ClassResults";

// #642 — THE app: one live, time-ordered running order for ONE ring.
//
// Read-only by construction. The announcer never writes a score, never changes a
// status, never touches FVD — which is what keeps this screen small.
//
// Freshness, per docs/score/announcer.md:
//   - ride STATUSES change constantly during a class -> poll every 5s
//     (useRideStatuses; it already pauses on a hidden tab and refreshes on
//     return, so nothing here needs a visibility listener of its own).
//   - rides / breaks / classes change on a scratch, a schedule reorder, or a
//     re-ingest -> cache-first from IndexedDB, revalidate on the SAME 5s beat
//     (#692 — a class reorder/scratch/moved break must show up on the very
//     next poll, and Evin needs to see an FVD change within 5s). people/horses
//     stay on their own one-shot fetch (useRoster.js) — org-wide roster data
//     that isn't part of the running order.
//
// #692 polling cost, one ring/terminal, one show day (all single-query GSI
// reads, no per-class fan-out — #637's showId/ring filter on GET /ride):
//   GET /ride?showId&ring      (ridesByShow GSI)    -> 720/hr
//   GET /break?showId&ring     (breaksByShow GSI)   -> 720/hr
//   GET /class?showId                               -> 720/hr
//   POST /rides/status (batch)                       -> 720/hr
//   ~2,880 reads/hr/terminal. Cheap: each is one indexed query, not a scan,
//   and a full 8-hour show day is well under 25k reads per terminal even with
//   several terminals running at once.
const STATUS_POLL_MS = 5000;
const ENTITY_POLL_MS = 5000;
// #645 — how long the screen can go without a successful status refresh
// before it stops calling itself "live", even while `online` is still true.
// A single missed poll (a blip) shouldn't flash the banner; four missed polls
// (STATUS_POLL_MS * 4) means something's actually wrong upstream.
const STALE_THRESHOLD_MS = STATUS_POLL_MS * 4;
// How often the banner's own clock re-checks staleness / re-renders its
// "HH:MM" text. Independent of the entity/status polls — this never touches
// the network, it just re-evaluates wall-clock math.
const CLOCK_TICK_MS = 10000;
// #691 — the sticky header's live clock + countdown want a proper ticking
// feel, so they get their own faster tick, separate from the freshness
// banner's coarser one above. Still fetches nothing — pure wall-clock math.
const LIVE_TICK_MS = 1000;

// #691 — Auto Scroll to Current Ride, persisted the same way
// AnnouncerContext persists the session: try/catch localStorage, JSON-free
// since it's a single boolean. Defaults ON — that's today's existing
// (previously unconditional) auto-scroll behavior, so turning this card on
// doesn't change anyone's experience until they choose to flip it off.
const AUTO_SCROLL_STORAGE_KEY = "announcer-fe.autoScrollEnabled";

function loadAutoScrollPreference() {
  try {
    const raw = localStorage.getItem(AUTO_SCROLL_STORAGE_KEY);
    return raw === null ? true : raw === "true";
  } catch {
    return true;
  }
}

function persistAutoScrollPreference(enabled) {
  try {
    localStorage.setItem(AUTO_SCROLL_STORAGE_KEY, String(enabled));
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
}

// "2h 5m" / "5m" — coarse on purpose; this is "how long until the next
// event", not a race-clock stopwatch.
function formatCountdown(ms) {
  if (ms <= 0) return "0m";
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

// The next event's own label — a break announces its label (or the generic
// "Break"), a ride announces its class number + rider, matching the fields
// already shown on its row.
function nextEventLabel(row, t) {
  if (!row) return "";
  if (row.kind === ROW_KIND.BREAK) return row.label || t("oog.breakLabel");
  return [row.classNumber, row.riderName].filter(Boolean).join(" — ");
}

// The entity-list revalidate beat (#692 — 5s, same cadence as the status poll).
// Paused while offline (there is nothing to revalidate against) and while the
// tab is hidden — an announcer who has switched away is not reading the
// screen, and #645's degraded mode leans on the cache staying put.
function useEntityTick(online) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!online) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      setTick((n) => n + 1);
    }, ENTITY_POLL_MS);
    return () => clearInterval(id);
  }, [online]);
  return tick;
}

// #645 — a plain wall clock, always ticking (unlike useEntityTick, which
// pauses offline — staleness must be detectable EXACTLY when we're offline).
// Purely a re-render trigger for the freshness banner's math; fetches nothing.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function handleSignOut() {
  logoutCognito({
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
    cognitoDomain: import.meta.env.VITE_COGNITO_AUTH_DOMAIN,
    logoutUri: import.meta.env.VITE_COGNITO_POST_LOGOUT_REDIRECT_URI,
  });
}

function ClassHeader({ row }) {
  const { t } = useTranslation("announcer");
  const heading = [row.classNumber, row.className].filter(Boolean).join(" — ");
  return (
    <Box data-testid="class-header" data-class-id={row.classId} sx={{ pt: 3, pb: 0.5 }}>
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 1 }}
      >
        {t("oog.classLabel")}
      </Typography>
      <Typography variant="h4" fontWeight={800} sx={{ color: "text.primary" }}>
        {heading || t("oog.classLabel")}
      </Typography>
      <Divider sx={{ mt: 1, borderBottomWidth: 2 }} />
    </Box>
  );
}

// A break is not a ride and never can be (#585/#638) — it gets its own row, and
// it is one of the things the announcer actually announces.
function BreakRow({ row }) {
  const { t } = useTranslation("announcer");
  return (
    <Box
      data-testid="break-row"
      data-break-id={row.id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: 3,
        border: "2px dashed",
        borderColor: colors.foxOrange,
        // Deliberately no fill: a tint that reads in light mode goes muddy in
        // dark, and the dashed orange border carries the signal in both.
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ minWidth: 96 }}>
        {formatTime(row.scheduledTime)}
      </Typography>
      <RestaurantIcon sx={{ color: colors.foxOrange }} />
      <Typography variant="h5" fontWeight={800} sx={{ color: "text.primary" }}>
        {row.label || t("oog.breakLabel")}
      </Typography>
    </Box>
  );
}

export default function OrderOfGo() {
  const navigate = useNavigate();
  const { t } = useTranslation("announcer");
  const { selectedShow, selectedRing, unlockPicker, online, prefetchStatus } =
    useAnnouncerContext();
  const showId = selectedShow?.id;

  // Static literal keys only — the message is chosen by branch, never by
  // t(`setup.${status}`).
  let prefetchMessage = "";
  if (prefetchStatus?.status === "loading") prefetchMessage = t("setup.prefetching");
  else if (prefetchStatus?.status === "error") prefetchMessage = t("setup.prefetchError");
  else if (prefetchStatus?.status === "done")
    prefetchMessage = t("setup.prefetchDone", {
      rides: prefetchStatus.counts?.rides ?? 0,
      breaks: prefetchStatus.counts?.breaks ?? 0,
    });

  // Entity lists move only on a scratch or a re-ingest, so a slow beat is plenty
  // (statuses have their own, much faster, poll below). Bumping the tick is what
  // makes useCachedResource revalidate — it re-runs on its deps.
  const entityTick = useEntityTick(online);

  // #692 — classes now revalidate on the same entityTick as rides/breaks
  // (previously it never accepted a nonce at all, so a renamed/renumbered or
  // newly-added class could sit stale in the header forever after setup).
  const { data: classes, loading: classesLoading } = useClassesForShow(
    showId,
    online,
    entityTick,
  );
  const { data: rides, loading: ridesLoading } = useRidesForRing(
    showId,
    selectedRing,
    online,
    entityTick,
  );
  const { data: breaks } = useBreaksForRing(showId, selectedRing, online, entityTick);
  const { data: people } = usePeople(online);
  const { data: horses } = useHorses(online);

  const testIds = useMemo(() => (rides || []).map((r) => r.testId).filter(Boolean), [rides]);
  const { data: tests } = useTestsForRides(testIds, online);

  // Poll the SINGLE server-derived status for every ride in the ring (#544/#546).
  // Only meaningful while online — a hidden tab is already handled inside the hook.
  const rideIds = useMemo(() => (rides || []).map((r) => r.id), [rides]);
  const { statuses, lastUpdated: statusesLastUpdated } = useRideStatuses(rideIds, {
    intervalMs: STATUS_POLL_MS,
    enabled: online && rideIds.length > 0,
  });

  // #645 — freshness banner. The status poll's own lastUpdated is the primary
  // source (it's the fastest-changing data, per docs/score/announcer.md's
  // freshness table); the setup-time prefetch timestamp is the fallback for a
  // reload that lands already offline, before any poll has ever landed.
  const lastPrefetchedAt = useLastPrefetchedAt(prefetchStatus?.status);
  const lastUpdated =
    statusesLastUpdated ?? (lastPrefetchedAt ? new Date(lastPrefetchedAt) : null);
  const now = useNow(CLOCK_TICK_MS);
  const isStale = Boolean(lastUpdated) && now - lastUpdated.getTime() > STALE_THRESHOLD_MS;
  // #691 — sticky header's live clock + countdown. Separate, faster tick from
  // the freshness banner's `now` above; pure re-render trigger, no network.
  const liveNow = useNow(LIVE_TICK_MS);
  // "Not live" whenever we're offline OR the data we're showing has gone
  // stale despite being nominally online (a dead upstream can still report
  // navigator.onLine === true) — the screen must never claim to be live when
  // it isn't sure.
  const showFreshnessBanner = !online || isStale;

  const rows = useMemo(
    () =>
      buildOrderOfGoRows({
        rides: rides || [],
        breaks: breaks || [],
        classes: classes || [],
        people: people || [],
        horses: horses || [],
        tests: tests || [],
        statuses: statuses || {},
      }),
    [rides, breaks, classes, people, horses, tests, statuses],
  );

  // #691 — sticky header's countdown: next ride OR break, whichever is
  // sooner (breaks are real `score-break` rows and already inline in `rows`).
  const nextEvent = useMemo(() => findNextEvent(rows, liveNow), [rows, liveNow]);

  // #644 — results/placings. The "live" class is the CURRENT ride's class:
  // that's the one whose results can still change from one status poll to the
  // next, so it's the only one that needs a slow results poll of its own
  // (docs/score/announcer.md freshness table). Every other class fetches once.
  const currentRideRow = rows.find(
    (r) => r.kind === ROW_KIND.RIDE && r.role === RIDE_ROLE.CURRENT,
  );
  const liveClassId = currentRideRow?.classId || null;

  const rideRowsByClassId = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      if (row.kind !== ROW_KIND.RIDE || !row.classId) continue;
      if (!map.has(row.classId)) map.set(row.classId, []);
      map.get(row.classId).push(row);
    }
    return map;
  }, [rows]);

  // classId -> raw GET /class/:classId/results array, lifted up from each
  // ClassResults instance so RideRow can badge its own placing too (the
  // class-level list and the per-ride badge are fed by the SAME fetch/poll,
  // never a second independent one).
  const [resultsByClassId, setResultsByClassId] = useState({});
  const handleClassResults = useCallback((classId, classResults) => {
    setResultsByClassId((prev) => {
      if (prev[classId] === classResults) return prev;
      return { ...prev, [classId]: classResults };
    });
  }, []);

  // ---- auto-scroll to the current ride, without ever fighting the announcer --
  const currentRideId = findRoleRideId(rows, RIDE_ROLE.CURRENT);
  const currentRef = useRef(null);
  const [currentOffScreen, setCurrentOffScreen] = useState(false);
  // #691 — the "Auto Scroll to Current Ride" switch. OFF leaves scrolling
  // entirely to the announcer (the effect below simply doesn't fire); ON
  // restores today's existing behavior. Persisted like AnnouncerContext's
  // session, so it survives a reload.
  const [autoScrollEnabled, setAutoScrollEnabledState] = useState(loadAutoScrollPreference);
  const setAutoScrollEnabled = useCallback((enabled) => {
    setAutoScrollEnabledState(enabled);
    persistAutoScrollPreference(enabled);
  }, []);

  const scrollToCurrent = useCallback(() => {
    currentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // Scroll only when the current ride CHANGES (and on first paint), and only
  // while the switch is ON. Deliberately NOT on every poll or scroll event —
  // yanking the view out from under someone reading a name aloud is worse
  // than making them tap once. Toggling the switch back ON re-fires this
  // (autoScrollEnabled is a dep) and snaps straight to the current ride.
  useEffect(() => {
    if (!currentRideId || !autoScrollEnabled) return;
    currentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [currentRideId, autoScrollEnabled]);

  useEffect(() => {
    if (!currentRideId) {
      setCurrentOffScreen(false);
      return undefined;
    }
    const check = () => {
      const el = currentRef.current;
      if (!el) return;
      const { top, bottom } = el.getBoundingClientRect();
      setCurrentOffScreen(bottom < 0 || top > window.innerHeight);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [currentRideId, rows.length]);

  const handleUnlock = () => {
    unlockPicker();
    navigate("/setup");
  };

  const loading = (classesLoading || ridesLoading) && rows.length === 0;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 12 }}>
      <PinLock onUnlock={handleUnlock} testId="announcer-lock" />

      {/* #691 — sticky header: everything that was previously "above the
          Classes list" (show/ring/sign-out, freshness banner, prefetch
          status), plus the live venue clock, the next-event countdown, and
          the Auto Scroll switch. Stays pinned while `oog-list` below scrolls
          under it — no AppBar in this app, so `top: 0` is correct. */}
      <Box
        data-testid="oog-sticky-header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          px: 3,
          pt: 3,
          pb: 2,
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={800} data-testid="oog-show-name">
                {selectedShow?.name}
              </Typography>
              <Chip
                data-testid="oog-ring"
                label={`${t("setup.ringLabel")}: ${selectedRing}`}
                sx={{
                  mt: 1,
                  fontSize: "1rem",
                  fontWeight: 800,
                  bgcolor: colors.foxOrange,
                  color: "#000",
                }}
              />
            </Box>

            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  data-testid="oog-clock"
                  sx={{ color: "text.primary", fontVariantNumeric: "tabular-nums" }}
                >
                  {formatVenueClock(liveNow)}
                </Typography>
                <Typography
                  variant="body2"
                  data-testid="oog-countdown"
                  sx={{ color: "text.secondary" }}
                >
                  {nextEvent
                    ? t("oog.nextEvent", {
                        label: nextEventLabel(nextEvent, t),
                        countdown: formatCountdown(
                          new Date(nextEvent.scheduledTime).getTime() - liveNow,
                        ),
                      })
                    : t("oog.nextEventNone")}
                </Typography>
              </Box>
              <Button
                variant="text"
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleSignOut}
                sx={{ color: "text.secondary" }}
              >
                {t("setup.signOut")}
              </Button>
            </Stack>
          </Stack>

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={autoScrollEnabled}
                onChange={(e) => setAutoScrollEnabled(e.target.checked)}
                data-testid="auto-scroll-toggle"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: colors.foxOrange },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: colors.foxOrange,
                  },
                }}
              />
            }
            label={t("oog.autoScrollLabel")}
            slotProps={{ typography: { sx: { color: "text.primary" } } }}
          />

          {/* #645 — persistent, honest freshness banner. Shows whenever the app
              is offline OR the cached data has gone stale despite being
              nominally online; clears itself the moment a refresh lands. */}
          {showFreshnessBanner ? (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="oog-not-live">
              {lastUpdated
                ? t("oog.notLive", { time: formatClockTime(lastUpdated) })
                : t("oog.notLiveNoData")}
            </Alert>
          ) : null}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1 }}
            data-testid="prefetch-status"
          >
            {prefetchMessage}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, maxWidth: 1100, mx: "auto" }}>
        {/* #714 — the running-order list not yet loaded: shaped skeleton
            rows instead of a bare spinner, matching the table-data
            convention used everywhere else. */}
        {loading ? (
          <Box sx={{ mt: 3 }} data-testid="oog-loading">
            <TableSkeleton rows={8} columns={4} />
          </Box>
        ) : null}

        {!loading && rows.length === 0 ? (
          <Typography sx={{ mt: 8 }} color="text.secondary" data-testid="oog-empty">
            {t("oog.empty")}
          </Typography>
        ) : null}

        <Stack spacing={1.5} sx={{ mt: 2 }} data-testid="oog-list">
          {rows.map((row) => {
            if (row.kind === ROW_KIND.CLASS_HEADER) {
              return (
                <Box key={row.id}>
                  <ClassHeader row={row} />
                  <ClassResults
                    classId={row.classId}
                    isLive={row.classId === liveClassId}
                    rideRows={rideRowsByClassId.get(row.classId) || []}
                    onResults={handleClassResults}
                    online={online}
                  />
                </Box>
              );
            }
            if (row.kind === ROW_KIND.BREAK) {
              return <BreakRow key={row.id} row={row} />;
            }
            const classResults = resultsByClassId[row.classId];
            const rideResult = classResults?.find((r) => r.rideId === row.id) || null;
            return (
              <RideRow
                key={row.id}
                row={row}
                result={rideResult}
                ref={row.role === RIDE_ROLE.CURRENT ? currentRef : null}
              />
            );
          })}
        </Stack>
      </Box>

      {currentOffScreen ? (
        <Fab
          variant="extended"
          data-testid="back-to-current"
          onClick={scrollToCurrent}
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: colors.foxOrange,
            color: "#000",
            fontWeight: 800,
            "&:hover": { bgcolor: colors.foxOrange },
          }}
        >
          <ArrowDownwardIcon sx={{ mr: 1 }} />
          {t("oog.backToCurrent")}
        </Fab>
      ) : null}
    </Box>
  );
}
